import React, { useState, useEffect, useRef } from 'react'
import './DetachedCallPopupWindow.css'

const CHANNEL_NAME = 'bridge_telephony_popup_channel'

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function getInitials(name) {
  if (!name) return '?'
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

export default function DetachedCallPopupWindow() {
  const [callState, setCallState] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('state') || 'RINGING'
  })
  const [callerName, setCallerName] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('name') || 'Customer'
  })
  const [callerNumber, setCallerNumber] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('number') || ''
  })
  const [direction, setDirection] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('direction') || 'inbound'
  })
  const [projectName, setProjectName] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('project') || ''
  })
  const [durationSecs, setDurationSecs] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [isOnHold, setIsOnHold] = useState(false)

  const channelRef = useRef(null)

  useEffect(() => {
    // Ensure window title is clean
    document.title = 'Active Call — Bridge Telephony'

    // Initialize BroadcastChannel
    const channel = new BroadcastChannel(CHANNEL_NAME)
    channelRef.current = channel

    channel.onmessage = (event) => {
      const msg = event.data || {}

      if (msg.type === 'SYNC_STATE') {
        const p = msg.payload || {}
        if (p.callState)        setCallState(p.callState)
        if (p.callerName)       setCallerName(p.callerName)
        if (p.callerNumber)     setCallerNumber(p.callerNumber)
        if (p.direction)        setDirection(p.direction)
        if (p.projectName)      setProjectName(p.projectName)
        if (p.durationSecs !== undefined) setDurationSecs(p.durationSecs)
        if (p.isMuted !== undefined)      setIsMuted(p.isMuted)
        if (p.isOnHold !== undefined)     setIsOnHold(p.isOnHold)
      } else if (msg.type === 'UPDATE_CALLER_INFO') {
        const p = msg.payload || {}
        if (p.callerName)       setCallerName(p.callerName)
        if (p.callerNumber)     setCallerNumber(p.callerNumber)
        if (p.projectName)      setProjectName(p.projectName)
      } else if (msg.type === 'TIMER_TICK') {
        if (msg.durationSecs !== undefined) {
          setDurationSecs(msg.durationSecs)
        }
      } else if (msg.type === 'CALL_CONNECTED') {
        setCallState('CONNECTED')
      } else if (msg.type === 'CALL_ENDED') {
        setCallState('ENDED')
        setTimeout(() => {
          try { window.close() } catch (_) {}
        }, 1500)
      } else if (msg.type === 'CALL_MISSED') {
        setCallState('MISSED')
        setTimeout(() => {
          try { window.close() } catch (_) {}
        }, 2500)
      } else if (msg.type === 'CLOSE_WINDOW') {
        try { window.close() } catch (_) {}
      }
    }

    // Announce to the master window that the popup has opened
    channel.postMessage({ type: 'POPUP_MOUNTED' })

    return () => {
      channel.close()
    }
  }, [])

  const sendAction = (action) => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type: 'CALL_ACTION', action })
    }
    if (action === 'decline') {
      setCallState('ENDED')
      setTimeout(() => {
        try { window.close() } catch (_) {}
      }, 800)
    } else if (action === 'end' || action === 'cancel_outbound') {
      setCallState('ENDED')
      setTimeout(() => {
        try { window.close() } catch (_) {}
      }, 1200)
    }
  }

  const handleManualClose = () => {
    try { window.close() } catch (_) {}
  }

  // ── Render States ──────────────────────────────────────────────────────────
  const isInbound = direction === 'inbound'
  const isRinging = callState === 'RINGING'
  const isCalling = callState === 'OUTBOUND_CALLING'
  const isConnected = callState === 'CONNECTED'
  const isEnded = callState === 'ENDED' || callState === 'MISSED'

  return (
    <div className="dpw-window-container">
      <div className={`dpw-card ${
        isRinging ? 'dpw-card--ringing' :
        isCalling ? 'dpw-card--calling' :
        isConnected ? 'dpw-card--connected' :
        'dpw-card--ended'
      }`}>

        {/* ── Top Header Bar ── */}
        <div className="dpw-header">
          {isRinging && (
            <span className="dpw-badge dpw-badge--inbound">
              <span className="dpw-status-indicator pulse" />
              Incoming Call
            </span>
          )}
          {isCalling && (
            <span className="dpw-badge dpw-badge--outbound">
              <span className="dpw-status-indicator pulse" />
              Outbound Calling
            </span>
          )}
          {isConnected && (
            <span className="dpw-badge dpw-badge--connected">
              <span className="dpw-status-indicator" />
              Connected
            </span>
          )}
          {isEnded && (
            <span className="dpw-badge dpw-badge--ended">
              <span className="dpw-status-indicator" />
              {callState === 'MISSED' ? 'Missed Call' : 'Call Ended'}
            </span>
          )}

          <button
            className="dpw-close-btn"
            onClick={handleManualClose}
            title="Close Window"
          >
            ✕
          </button>
        </div>

        {/* ── Avatar & Pulse Animation ── */}
        <div className="dpw-avatar-section">
          <div className="dpw-avatar-wrapper">
            {(isRinging || isCalling) && <div className="dpw-pulse-ring" />}
            <div className={`dpw-avatar ${
              isRinging ? 'ringing' :
              isCalling ? 'calling' :
              isConnected ? 'connected' : ''
            }`}>
              {getInitials(callerName)}
            </div>
          </div>

          {/* Caller Details */}
          <div className="dpw-caller-info">
            <h2 className="dpw-caller-name">{callerName}</h2>
            {callerNumber && <p className="dpw-caller-number">{callerNumber}</p>}
            {projectName && <p className="dpw-caller-sub">📁 {projectName}</p>}
          </div>

          {/* Live Timer if Connected */}
          {isConnected && (
            <div className="dpw-timer-badge">
              <span className="dpw-timer-dot" />
              {formatDuration(durationSecs)}
            </div>
          )}
        </div>

        {/* ── Action Buttons ── */}
        {isRinging && (
          <div className="dpw-actions">
            <button
              className="dpw-circle-btn dpw-btn--decline"
              onClick={() => sendAction('decline')}
              title="Decline Call"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
              <span className="dpw-btn-label">Decline</span>
            </button>

            <button
              className="dpw-circle-btn dpw-btn--answer"
              onClick={() => sendAction('answer')}
              title="Answer Call"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25 1.12.37 2.32.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <span className="dpw-btn-label">Answer</span>
            </button>
          </div>
        )}

        {isCalling && (
          <div className="dpw-actions">
            <button
              className="dpw-circle-btn dpw-btn--end"
              onClick={() => sendAction('cancel_outbound')}
              title="Cancel Call"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
              <span className="dpw-btn-label">Cancel</span>
            </button>
          </div>
        )}

        {isConnected && (
          <div className="dpw-actions">
            <div className="dpw-ctrl-group">
              {/* Mute Button */}
              <button
                className={`dpw-ctrl-btn ${isMuted ? 'active' : ''}`}
                onClick={() => sendAction('toggle_mute')}
                title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
              >
                {isMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.14 1.76-.46 2.53-.93L19.73 21 21 19.73 4.27 3z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"/>
                  </svg>
                )}
              </button>

              {/* End Call Button */}
              <button
                className="dpw-circle-btn dpw-btn--end"
                onClick={() => sendAction('end')}
                title="End Active Call"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
                </svg>
                <span className="dpw-btn-label">End</span>
              </button>

              {/* Hold Button */}
              <button
                className={`dpw-ctrl-btn ${isOnHold ? 'active' : ''}`}
                onClick={() => sendAction('toggle_hold')}
                title={isOnHold ? 'Resume Call' : 'Hold Call'}
              >
                {isOnHold ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
